import React, { useState, useEffect } from 'react';
import ReportedDisabledTransactionDetails from './components/ReportedDisabledTransactionDetails';
import ReportedDisabledTransactionSkusList from './components/ReportedDisabledTransactionSkusList';
import KeyProcessProgressBar from './components/KeyProcessProgressBar';
import ActionBar from '../layout/ActionBar';
import UploadDisabled from './slideout/UploadDisabled';
import KwPanel from '../common/KwPanel';
import { toast } from 'react-toastify';
import SlidingPane from 'react-sliding-pane';
import DisableOnSteam from './slideout/DisableOnSteam';
import DisableKeyProviderResults from './components/DisableKeyProviderResults';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DisabledTransaction,
  DisabledTransactionSKU,
} from '../../types/disabledTransaction';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  addToGroup,
  removeFromGroup,
} from '../../middleware/signalRMiddleware';
import {
  authoriseDisabledTransaction,
  downloadDisabledKeys,
  downloadDisabledKeysForSkuAndCompany,
  getDisabledTransactionById,
} from '../../api/keyCodeApi';
import { clearDisabledTransaction } from '../../reducers/disabledTransactionReducer';
import { hasPermission } from '../auth/authUtils';
import permissionTypes from '../../constants/permissionTypes';

function ReportedDisabledTransaction() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pageTitle, setPageTitle] = usePageTitle([
    'Reported disabled transaction',
    'Keys',
  ]);
  const { t } = useTranslation();

  const disabledTransactions = useAppSelector(
    (state) => state.disabledTransactions
  );
  const currentUser = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const [transaction, setTransaction] = useState<
    DisabledTransaction | undefined
  >();
  const [reload, setReload] = useState<Date | undefined>();
  const [uploadSlideoutOpen, setUploadSlideoutOpen] = useState(false);
  const [disableOnSteamSlideoutOpen, setDisableOnSteamSlideoutOpen] =
    useState(false);
  const [isWaitingForSteam, setIsWaitingForSteam] = useState(false);

  useEffect(() => {
    if (id) {
      // get transaction
      getDisabledTransactionById(id)
        .then((d) => {
          setTransaction(d);
          setPageTitle([
            d.keyProviderName,
            'Reported disabled transaction',
            'Keys',
          ]);
        })
        .catch((e) => {
          console.error(e);
          navigate('/keys/reported-disabled/');
          toast.error(t('Transaction not found'));
        });
    }
  }, [id, reload, t, setPageTitle, navigate]);

  useEffect(() => {
    if (
      transaction &&
      (transaction.status === 'Pending' || isWaitingForSteam)
    ) {
      const key = `disabled:${transaction.id}`;
      dispatch(addToGroup(key));
      return () => {
        dispatch(removeFromGroup(key));
      };
    }
  }, [transaction, isWaitingForSteam, dispatch]);

  useEffect(() => {
    if (!disabledTransactions || disabledTransactions.length === 0) {
      return;
    }

    const disabledTransaction = disabledTransactions.find((x) => x.id === id);

    if (!disabledTransaction) {
      return;
    }

    if (disabledTransaction.status === 'reload') {
      setReload(new Date());
    } else {
      setTransaction((prevState) => {
        return {
          ...prevState,
          ...disabledTransaction,
        };
      });
    }

    return () => {
      dispatch(clearDisabledTransaction(id ?? ''));
    };
  }, [disabledTransactions, id, dispatch]);

  const handleDownloadKeys = (event: any) => {
    event.cancelBubble = true;
    if (!id) return;
    downloadDisabledKeys(id).catch((e) => {
      toast.error(e.message || t('Failed to download keys'));
    });
  };

  const handleDownloadItem = (event: any, item: DisabledTransactionSKU) => {
    event.cancelBubble = true;
    if (!id) return;
    downloadDisabledKeysForSkuAndCompany(id, item.skuId, item.companyId).catch(
      () => {
        toast.error(t('Failed to download keys'));
      }
    );
  };

  const handleAuthorise = (event: any) => {
    event.cancelBubble = true;
    if (!id) return;
    authoriseDisabledTransaction(id)
      .then((authorised) =>
        setTransaction({
          ...transaction,
          ...(authorised as Record<string, unknown>),
        } as DisabledTransaction)
      )
      .catch((e) => {
        toast.error(e.message || t('Failed to authorise transaction'));
      });
  };

  return (
    <>
      <ActionBar
        breadcrumb={[
          {
            link: '/keys/reported-disabled',
            label: t('Disabled transactions'),
          },
          { label: t('Disabled transaction') },
        ]}
      >
        {transaction?.steamIntegrationEnabled && (
          <Button
            className="btn btn-default me-2"
            onClick={() => setDisableOnSteamSlideoutOpen(true)}
            isDisabled={
              !transaction ||
              (!transaction.success && !transaction.partialSuccess)
            }
            text={t('Disable on Steam')}
          />
        )}
        <Button
          className="btn btn-default me-2"
          onClick={handleDownloadKeys}
          isDisabled={
            !transaction ||
            (!transaction.success && !transaction.partialSuccess)
          }
          text={t('Download')}
        />
        <Button
          className="btn btn-primary"
          onClick={() => {
            setUploadSlideoutOpen(true);
          }}
          text={t('Disable more keys')}
        />
      </ActionBar>
      {transaction && (
        <>
          <ReportedDisabledTransactionDetails
            transaction={transaction}
            currentUser={currentUser}
          />
          {!transaction.isAuthorised && hasPermission(currentUser, permissionTypes.MANAGE_KEYS) ? (
            <KwPanel>
              <header className="mb-2">
                <h2>{t('Authorise transaction')}</h2>
              </header>
              <Button
                className="btn btn-primary"
                onClick={handleAuthorise}
                text={t('Authorise')}
              />
            </KwPanel>
          ) : (
            <KeyProcessProgressBar transaction={transaction} />
          )}
          {(transaction.success || transaction.partialSuccess) && (
            <ReportedDisabledTransactionSkusList
              id={id}
              reload={reload}
              handleDownload={handleDownloadItem}
            />
          )}
          {transaction.results && transaction.results.length > 0 && (

            <DisableKeyProviderResults results={transaction.results} />
          )}
        </>
      )}

      <SlidingPane
        isOpen={uploadSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setUploadSlideoutOpen(false)}
      >
        <UploadDisabled
          done={() => {
            setUploadSlideoutOpen(false);
            setReload(new Date());
          }}
          cancel={() => setUploadSlideoutOpen(false)}
        />
      </SlidingPane>

      <SlidingPane
        isOpen={disableOnSteamSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setDisableOnSteamSlideoutOpen(false)}
      >
        <DisableOnSteam
          id={id}
          done={() => {
            setDisableOnSteamSlideoutOpen(false);
            setIsWaitingForSteam(true);
          }}
          cancel={() => setDisableOnSteamSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  );
}

export default ReportedDisabledTransaction;
